import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Combobox,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Toolbar,
  type ComboboxOption,
} from '@biovet/ui';
import { ApiError } from '../lib/api.js';
import type { PrintColumn, PrintDocument } from '../lib/print.js';
import { defaultUom } from '../lib/reference-data.js';
import { Fact, Th } from './parts.js';

/**
 * The chrome every operational document screen wears.
 *
 * Sales, purchase and inventory are eleven registers that each carry one or two
 * writes, and they all need the same four things: a busy flag, the last thing
 * that worked, the last thing that was refused, and a panel to type into. The
 * brokerage screens grew their own copy of the first three before there was a
 * second module to share them with; this is that copy, moved to where three
 * modules can read it.
 *
 * A refusal keeps the database's own sentence. `0015_invariants.sql` writes its
 * messages for an accountant — "Insufficient stock: SBM-ARG-50 at Karachi Main
 * Warehouse has 12,400.000 available, 15,000.000 requested" — and replacing that
 * with "422 Unprocessable Entity" throws away the only part of the system that
 * knows why the posting failed.
 */

export function useAction(reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setNotice(null);
    setProblem(null);
    try {
      const result = await work();
      setNotice(result.message);
      reload();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { busy, notice, problem, run, clear: () => (setNotice(null), setProblem(null)) };
}

/**
 * Ctrl/Cmd + Enter posts the document that is open.
 *
 * A register's Post button sits under a forty-line grid, and reaching it means
 * leaving the keyboard, scrolling, aiming, and coming back — per document, all
 * day. The shortcut is not a power-user affordance here; it is the difference
 * between typing an invoice and operating one.
 *
 * Listening on `document` rather than on the panel is deliberate: the operator
 * may be in any cell of the grid when they finish, and a handler bound to the
 * form would miss a focus that has wandered into a portalled dropdown. `enabled`
 * carries the same condition the button's `disabled` does, so the two can never
 * disagree about whether the document is postable.
 */
export function useSubmitShortcut(enabled: boolean, submit: () => void) {
  // Held in a ref so a new closure each render does not re-bind the listener.
  const latest = useRef(submit);
  latest.current = submit;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      latest.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** The hint that makes the shortcut discoverable rather than folklore. */
export function SubmitHint() {
  return (
    <span className="text-[11px] whitespace-nowrap text-[var(--ink-3)]" data-noprint="1">
      <kbd className="bb-mono">Ctrl</kbd> + <kbd className="bb-mono">Enter</kbd> to post
    </span>
  );
}

export function Messages({ notice, problem }: { notice: string | null; problem: string | null }) {
  if (!notice && !problem) return null;
  return (
    <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
      {problem && (
        <Callout tone="danger" title="That was refused">
          {problem}
        </Callout>
      )}
      {notice && (
        <Callout tone="success" title="Done">
          {notice}
        </Callout>
      )}
    </div>
  );
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">{children}</div>;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The collapsible entry panel a register opens above its grid.
 *
 * Closed by default, because these screens are read far more often than they are
 * written to, and a register that opens with a blank form on it has buried the
 * thing somebody came for.
 *
 * **There is no Close button in here.** There were two, one above the other:
 * `PanelToggle` in the screen's own action bar already reads `Close` while the
 * panel is open, and it sits a few pixels away in the page header. Two controls
 * with the same word doing the same thing is not redundancy, it is a question
 * about whether they differ. The one that stays is the one that is in the same
 * place whether the panel is open or shut.
 *
 * `onToggle` is still taken, and is what `Esc` calls — closing a form somebody
 * is typing into should always have a keyboard answer.
 */
export function EntryPanel({
  title,
  subtitle,
  open,
  onToggle,
  openLabel,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  /** Unused here — the screen's action bar owns the opening label. */
  openLabel: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  // Held in a ref so the listener binds once rather than on every keystroke in
  // the form underneath it.
  const close = useRef(onToggle);
  close.current = onToggle;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      // Not while a dropdown is showing: Esc belongs to the picker first, and a
      // combobox that closes the whole form instead of its own list is a trap.
      const expanded = (event.target as HTMLElement | null)?.getAttribute('aria-expanded');
      if (event.key !== 'Escape' || expanded === 'true') return;
      close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {actions}
            <span className="text-[11px] whitespace-nowrap text-[var(--ink-3)]">
              <kbd className="bb-mono">Esc</kbd> to close
            </span>
          </>
        }
      />
      <CardBody className="flex flex-col gap-3.5">{children}</CardBody>
    </Card>
  );
}

/** `openLabel` is unused when closed; the register's own action bar opens it. */
export function PanelToggle({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <Button size="sm" variant={open ? 'secondary' : 'primary'} onClick={onToggle}>
      {open ? 'Close' : label}
    </Button>
  );
}

/** A row of header fields, four across on a wide screen. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'number';
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/**
 * A field the server owns: shown, never typed.
 *
 * A disabled `Input` would read as a control that is temporarily unavailable —
 * something that might become editable if you did the right thing first. This is
 * not that: an issued SKU or barcode is identity, and the answer to "can I change
 * it" is no rather than not yet. So it renders as a value in the form's own
 * monospace rather than as a greyed-out box, and a value that does not exist yet
 * says what will produce it instead of sitting empty.
 */
export function ReadOnlyField({
  label,
  value,
  hint,
  placeholder,
}: {
  label: string;
  value: string | null;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="bb-row-h flex items-center">
        {value ? (
          <span className="bb-mono text-[13px] text-[var(--ink)]">{value}</span>
        ) : (
          <span className="text-[12.5px] text-[var(--ink-3)] italic">{placeholder ?? '—'}</span>
        )}
      </div>
    </Field>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  required,
  placeholder = '—',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * A header field you type into rather than scroll through.
 *
 * The same shape as `SelectField` so a swap is one word, and the reason to make
 * the swap is arithmetic: a native `<select>` costs two clicks — open, then aim
 * — plus however far the eye has to travel through a list of four hundred
 * customers, and the browser's own type-ahead only matches from the first
 * character of the label. This costs zero clicks and matches on any word.
 *
 * Keep `SelectField` for genuinely short closed sets: currency, a payment mode,
 * three units of measure. A dropdown of three is faster than a search box.
 */
export function ComboField({
  label,
  value,
  onChange,
  options,
  hint,
  required,
  placeholder = 'Type to find…',
  disabled,
  caption,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  caption?: string;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Combobox
        variant="field"
        value={value}
        options={options}
        placeholder={placeholder}
        caption={caption}
        disabled={disabled}
        aria-label={label}
        onChange={onChange}
      />
    </Field>
  );
}

/** Just the unit columns of a product, so every screen's own row type fits. */
export interface ProductUnits {
  sku: string;
  baseUom?: string;
  stockUom?: string | null;
  purchaseUom?: string | null;
  saleUom?: string | null;
}

/** Which of a product's three units a document should open on. */
export type LineRole = 'PURCHASE' | 'SALE' | 'STOCK';

/**
 * What a line should hold the moment an item is chosen.
 *
 * Two things, and both were being left blank for the operator to type on every
 * single line of every document:
 *
 * · **Quantity `1`.** Not because one is a likely order — it is the *unit* of
 *   ordering, and a line that starts at 1 is a line you edit rather than a line
 *   you have to notice is empty. Every screen already filters incomplete lines
 *   out of its payload, so a 1 on an abandoned row costs nothing.
 *   A quantity that is already typed is never overwritten: re-picking the item
 *   on a line somebody has entered 250 on must not silently reset it.
 *
 * · **The unit.** In preference order: the product's own unit *for this kind of
 *   document* — a feed bought by the ton and sold by the bag has both, and
 *   which one is right depends on which document you are on — then the company
 *   default from Setup → Company Profile, then the first unit in the register.
 *   `defaultUom()` owns that ladder; this only decides which of the product's
 *   three units to offer it.
 *
 * The unit *does* follow a re-pick, unlike the quantity. It is a property of
 * the item, so changing the item changes the right answer; the quantity is
 * something a person decided.
 */
export function pickedLine(
  sku: string,
  products: ProductUnits[] | undefined,
  role: LineRole,
  currentQty: string,
): { qty: string; uom: string } {
  const product = (products ?? []).find((p) => p.sku === sku);
  const own =
    role === 'PURCHASE'
      ? (product?.purchaseUom ?? product?.stockUom)
      : role === 'SALE'
        ? (product?.saleUom ?? product?.stockUom)
        : product?.stockUom;

  // The base unit is the last word rather than the first, but it is the only one
  // guaranteed to post: Product Management no longer asks for the three
  // presentation units, so on a book kept after `0036` `own` is undefined and
  // this is the whole answer. Where one is still stored — a product created
  // before that — it is preferred, because a feed bought by the ton and sold by
  // the bag is a real distinction and the conversions for both now exist.
  const preferred = own ?? product?.baseUom ?? null;

  return {
    qty: currentQty.trim() ? currentQty : '1',
    // The company default cannot override the item's own unit: it may be in
    // another dimension entirely, which is a line that will not post.
    uom: product?.baseUom ? (preferred ?? product.baseUom) : defaultUom(preferred),
  };
}

/**
 * The grid grows its own last row.
 *
 * Every entry screen had an `Add line` button, and every one of them was a click
 * spent saying "yes, I have more" — which the grid can see for itself the moment
 * the last row stops being blank. So there is always exactly one empty row at the
 * bottom, and filling it makes another.
 *
 * The empty row is not junk on save: each screen already filters incomplete
 * lines out of its payload, which is what made the button removable rather than
 * merely annoying.
 */
export function useAutoRows<T>(
  rows: T[],
  setRows: (rows: T[]) => void,
  blank: () => T,
  isBlank: (row: T) => boolean,
) {
  // Read through a ref so the effect depends on the rows alone — a screen that
  // rebuilds `blank` every render must not re-append on every render.
  const make = useRef(blank);
  make.current = blank;
  const test = useRef(isBlank);
  test.current = isBlank;

  useEffect(() => {
    const last = rows[rows.length - 1];
    if (rows.length === 0 || (last !== undefined && !test.current(last))) {
      setRows([...rows, make.current()]);
    }
    // `setRows` is a fresh closure each render on most of these screens; the
    // rows are what decides whether to append.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
}

/**
 * Fill in the unit on rows that were built before the registry arrived.
 *
 * A screen's `useState([blankLine()])` runs at **mount**, and on a cold page
 * load that is before `GET /api/context` has answered — so `defaultUom()`
 * returns `''`, the row is stored with no unit, and a `<select>` whose value
 * matches none of its options silently displays the *first* one. The effect was
 * a stock transfer that said `BAG` on a book whose house unit is `PC`: not a
 * blank waiting to be filled, but a confident wrong answer.
 *
 * This is the same trap `App` documents around the chart of accounts — a value
 * derived once against an empty module-level registry is never recomputed,
 * because filling that registry changes no prop. The difference is that this
 * one is *state*, so it cannot be re-derived during render; it has to be
 * back-filled when the registry lands, which is what this does.
 *
 * Rows appended later by `useAutoRows` are already right — by then the registry
 * is loaded — and a row whose item has been chosen took its unit from
 * `pickedLine`. This only ever touches a row that has no unit at all.
 */
export function useDefaultUnits<T extends { uomCode: string }>(
  rows: T[],
  setRows: (rows: T[]) => void,
) {
  const unit = defaultUom();
  const apply = useRef(setRows);
  apply.current = setRows;

  useEffect(() => {
    if (!unit || !rows.some((row) => row.uomCode === '')) return;
    apply.current(rows.map((row) => (row.uomCode === '' ? { ...row, uomCode: unit } : row)));
    // `setRows` is a fresh closure each render on these screens, as in
    // `useAutoRows`; the rows and the resolved unit are what decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, unit]);
}

/**
 * `—` for an amount that is genuinely absent rather than zero.
 *
 * Zero and nothing are different facts on a register: a line with no tax is not
 * the same as a line whose tax has not been worked out, and printing `0.00` for
 * both loses the distinction on exactly the reports that need it.
 */
export function dash(value: string | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : value;
}

/**
 * The register shell every master screen shares.
 *
 * Branches, currencies, units, product categories and the party register are the
 * same screen with different columns — a list, a "show inactive" chip, a New
 * button and a row of actions — and a copy per screen would have been a copy per
 * screen to fix the next layout bug in. It started private to `Setup.tsx` and
 * moved here when Inventory and Sales & Purchase grew registers of their own.
 * The rows themselves stay with their screen, because that is where the columns
 * and the meaning of each count actually live.
 */
export function MasterTable({
  title,
  subtitle,
  loading,
  error,
  empty,
  columns,
  onNew,
  newLabel,
  toggle,
  children,
}: {
  title: string;
  subtitle: string;
  loading: boolean;
  error: string | undefined;
  empty: string;
  columns: string[];
  onNew?: () => void;
  newLabel: string;
  toggle: { on: boolean; onToggle: () => void; label: string };
  children: ReactNode;
}) {
  const rows = Array.isArray(children) ? children : [children];
  const hasRows = rows.flat().filter(Boolean).length > 0;

  return (
    <Card flush data-rpt="1">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Toolbar>
            <Button size="sm" variant={toggle.on ? 'secondary' : 'ghost'} onClick={toggle.onToggle}>
              {toggle.label}
            </Button>
            <Button size="sm" variant="primary" disabled={!onNew} onClick={onNew}>
              {newLabel}
            </Button>
          </Toolbar>
        }
      />
      {loading ? (
        <SkeletonRows rows={6} />
      ) : error ? (
        <CardBody>
          <Callout tone="danger" title="That did not load">
            {error}
          </Callout>
        </CardBody>
      ) : !hasRows ? (
        <EmptyState title={empty} description="Nothing to show yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr className="bg-[var(--surface-2)]">
                {columns.map((c, i) => (
                  <Th key={c || i} align={i >= 3 && i < columns.length - 1 ? 'right' : 'left'}>
                    {c}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Reading a document that has already been written ─────────────────────────

/**
 * One document, on screen and on paper, from a single description of it.
 *
 * Every one of the five operational registers could produce a document and none
 * of them could show you one: a purchase invoice was a row in a grid, and the
 * eleven figures that make it an invoice — the lines, the tax, what it came to —
 * existed only inside the panel that had posted it, which is gone by the time
 * anybody wants to look. `GET /api/purchase`, `/api/invoice` and `/api/shipment`
 * have answered for one document since they were written and nothing called
 * them; the two returns had no detail read at all.
 *
 * **The screen and the paper are rendered from the same object.** The caller
 * builds one `PrintDocument` — the facts, the columns, the rows, the totals —
 * and this renders it while `printDocument()` prints it. That is the whole
 * reason the shape is shared: a printed invoice that fills in a column the
 * screen does not have, or rounds one differently, is a document that disagrees
 * with the system it came out of, and the only way to be sure it cannot is for
 * there to be one description of it.
 */
export function DocumentPanel({
  doc,
  internal,
  loading = false,
  error,
  actions,
  onClose,
}: {
  doc: PrintDocument | null;
  /**
   * Columns that are ours and not the customer's.
   *
   * A sales invoice carries COGS, gross margin and the salesperson's commission,
   * and every one of those would be a disclosure on the copy that goes out of
   * the building. They are appended to the table on screen and are deliberately
   * not part of `doc`, which is the only thing that reaches the printer — so the
   * separation is structural rather than a rule somebody has to remember. `rows`
   * is parallel to `doc.rows`.
   */
  internal?: { columns: PrintColumn[]; rows: string[][] };
  loading?: boolean;
  error?: string;
  /** Print, delete, edit — whatever this register can do to the document. */
  actions?: ReactNode;
  onClose?: () => void;
}) {
  if (error) {
    return (
      <Callout tone="danger" title="This document could not be read">
        {error}
      </Callout>
    );
  }
  if (loading || !doc) {
    return (
      <Card flush className="mb-3.5" data-noprint="1">
        <CardBody>
          <SkeletonRows rows={5} cols={5} />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={`${doc.documentNo} · ${doc.title}`}
        subtitle={doc.watermark ?? undefined}
        actions={
          <>
            {actions}
            {onClose && (
              <Button size="sm" variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
          </>
        }
      />
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {doc.facts
            .filter((f) => f.value)
            .map((f) => (
              <Fact key={f.label} label={f.label} value={f.value} />
            ))}
        </div>
      </CardBody>

      {(() => {
        const columns = [...doc.columns, ...(internal?.columns ?? [])];
        const rows = doc.rows.map((row, i) => [...row, ...(internal?.rows[i] ?? [])]);
        return (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  {columns.map((c, i) => (
                    <Th
                      key={`${c.label}-${i}`}
                      align={c.align === 'right' ? 'right' : 'left'}
                      width={c.width}
                    >
                      {c.label}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr className="bb-row-h border-b border-[var(--line-2)]">
                    <td
                      colSpan={columns.length}
                      className="px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]"
                    >
                      This document has no lines.
                    </td>
                  </tr>
                )}
                {rows.map((row, i) => (
                  <tr key={i} className="bb-row-h border-b border-[var(--line-2)]">
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={
                          columns[j]?.align === 'right'
                            ? 'bb-num px-2.5 text-right text-[12.5px] tabular-nums'
                            : 'px-2.5 text-[12.5px]'
                        }
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {(doc.totals?.length || doc.narration || doc.notes?.length) && (
        <CardBody className="grid gap-3 md:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-2">
            {doc.narration && (
              <p className="m-0 text-[12.5px]">
                <span className="text-[var(--ink-3)]">Narration · </span>
                {doc.narration}
              </p>
            )}
            {doc.notes?.map((note) => (
              <p key={note} className="m-0 text-[12px] text-[var(--ink-3)]">
                {note}
              </p>
            ))}
            {internal && internal.columns.length > 0 && (
              <p className="m-0 text-[12px] text-[var(--ink-3)]">
                {internal.columns.map((c) => c.label).join(', ')}{' '}
                {internal.columns.length === 1 ? 'is' : 'are'} ours — not on the printed copy.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            {doc.totals?.map((t) => (
              <div
                key={t.label}
                className={
                  t.strong
                    ? 'flex justify-between border-t border-[var(--line-2)] pt-2 text-[13px] font-semibold'
                    : 'flex justify-between text-[12.5px]'
                }
              >
                <span className={t.strong ? undefined : 'text-[var(--ink-2)]'}>{t.label}</span>
                <span className="bb-num shrink-0 pl-3 tabular-nums">{t.value}</span>
              </div>
            ))}
          </div>
        </CardBody>
      )}
    </Card>
  );
}
